import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild, ChangeDetectorRef, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { ReactiveFormsModule, FormBuilder, FormGroup, FormControl } from '@angular/forms';
import { MatSelectModule, MatSelect } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subject, takeUntil, Observable } from 'rxjs';
import { startWith, map } from 'rxjs/operators';
import { LocalizacaoService } from '../../services/localizacao/localizacao.service';
import { CondicaoQualidadeStateService } from '../../services/condicao-qualidade-state/condicao-qualidade-state.service';
import { AcessoOfertaEducacionalStateService } from '../../services/acesso-oferta-educacional-state/acesso-oferta-educacional-state.service';
import { PrecoEncargosAdicionaisStateService } from '../../services/preco-encargos-adicionais-state/preco-encargos-adicionais-state.service';
import { Estado, Cidade, PlanoEducacional, LocalizacaoSelecionada, Escola } from '../../interfaces/location.interface';
import { TipoSimulacao } from '../../enums/simulation-type.enum';
import { EscolasService } from '../../services/escolas/escolas.service';
import { FeatureFlagService } from '../../services/feature-flag/feature-flag.service';

@Component({
  selector: 'app-selecionar-local',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './selecionar-local.component.html',
  styleUrl: './selecionar-local.component.scss'
})
export class SelecionarLocalComponent implements OnInit, OnDestroy {
  @ViewChild('selectEstado') selectEstado!: MatSelect;
  @ViewChild('selectCidade') selectCidade!: MatSelect;
  @ViewChild('selectPlanoEducacional') selectPlanoEducacional!: MatSelect;
  @ViewChild('selectEscola') selectEscola!: MatSelect;

  // ViewChild para inputs
  @ViewChild('inputEstadoAutocomplete') inputEstadoAutocomplete!: ElementRef<HTMLInputElement>;
  @ViewChild('inputCidadeAutocomplete') inputCidadeAutocomplete!: ElementRef<HTMLInputElement>;
  @ViewChild('inputEscolaAutocomplete') inputEscolaAutocomplete!: ElementRef<HTMLInputElement>;

  private destroy$ = new Subject<void>();

  formularioLocalizacao: FormGroup;

  // FormControls para autocomplete dos inputs
  estadoAutocompleteControl = new FormControl<string | Estado>('');
  cidadeAutocompleteControl = new FormControl<string | Cidade>('');
  escolaAutocompleteControl = new FormControl<string | Escola>('');

  estados: Estado[] = [];
  cidades: Cidade[] = [];
  planosEducacionais: PlanoEducacional[] = [];
  escolas: Escola[] = [];

  // Observables filtrados
  estadosFiltrados$!: Observable<Estado[]>;
  cidadesFiltradas$!: Observable<Cidade[]>;
  escolasFiltradas$!: Observable<Escola[]>;

  carregandoEstados = false;
  carregandoCidades = false;
  carregandoEscolas = false;

  readonly TipoSimulacao = TipoSimulacao;

  // Controla se deve mostrar o dropdown de escolas (apenas em /filtrar-escolas)
  get mostrarSeletorEscolas(): boolean {
    return this.router.url === '/filtrar-escolas';
  }

  // Controla se estamos no R3 (filtrar-instituicoes)
  get ehR3(): boolean {
    return this.router.url === '/filtrar-instituicoes';
  }

  get ehR2(): boolean {
    return this.router.url === '/selecionar-local';
  }

  get ehR5(): boolean {
    return (
      this.featureFlagService.funcionalidadeHabilitada(
        '880-seleciona-local-receitas-despesas',
      ) &&
      this.router.url === '/selecionar-local-R5'
    );
  }

  // Propriedades para controlar o breadcrumb
  get middleBreadcrumbText(): string {
    switch (this.router.url) {
      case '/filtrar-instituicoes':
        return 'Financiamento atual x necessário';
      case '/selecionar-local':
        return 'Módulo de Orçamento para garantia de um padrão de qualidade';
      case '/filtrar-escolas':
        return 'Quanto custa uma escola pública em condições de qualidade?';
      case '/selecionar-local-R5':
        return 'Receitas e despesas da Educação Básica';
      default:
        return '';
    }
  }

  get middleBreadcrumbLink(): string {
    switch (this.router.url) {
      case '/filtrar-instituicoes':
        return '/financiamento-atual-necessario';
      case '/selecionar-local':
        return '/planejamento-orcamentario-apresentacao';
      case '/filtrar-escolas':
        return '/custo-por-escola-apresentacao';
      case '/selecionar-local-R5':
        if (
          this.featureFlagService.funcionalidadeHabilitada(
            '880-seleciona-local-receitas-despesas',
          )
        ) {
          return '/relatorio-receitas-despesas-apresentacao';
        } else {
          return '/';
        }
      default:
        return '/';
    }
  }

  get breadcrumbText(): string {
    return 'Selecione uma localidade';
  }

  get subtitleText(): string {
    switch (this.router.url) {
      case '/filtrar-instituicoes':
        return 'Para começar a usar basta escolher uma localidade abaixo e simular o orçamento necessário';
      case '/selecionar-local':
        return 'Para começar a usar basta escolher uma localidade abaixo ou simular o planejamento orçamentário em nível nacional';
      case '/filtrar-escolas':
        return 'Para começar a usar basta escolher uma localidade e simular o custo por escola';
      case '/selecionar-local-R5':
        if (
          this.featureFlagService.funcionalidadeHabilitada(
            '880-seleciona-local-receitas-despesas',
          )
        ) {
          return 'Para começar a usar basta escolher uma localidade abaixo ou simular as receitas e despesas em nível nacional';
        } else {
          return '';
        }
      default:
        return '';
    }
  }

  constructor(
    private fb: FormBuilder,
    private localizacaoService: LocalizacaoService,
    private escolasService: EscolasService,
    private condicaoQualidadeState: CondicaoQualidadeStateService,
    private acessoOfertaEducacionalState: AcessoOfertaEducacionalStateService,
    private precoEncargosAdicionaisState: PrecoEncargosAdicionaisStateService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private featureFlagService: FeatureFlagService,
  ) {
    this.formularioLocalizacao = this.fb.group({
      pais: ['Brasil'],
      estado: [null],
      cidade: [{ value: null, disabled: true }],
      planoEducacional: [{ value: null, disabled: true }],
      escola: [{ value: null, disabled: true }],
    });
  }

  ngOnInit(): void {
    this.localizacaoService.limparLocalizacao(); // Sempre limpa a consulta ao entrar na página
    this.carregarEstados();
    this.carregarDadosIniciais();
    this.atualizarPlanosEducacionais();
    this.inicializarFiltros();
  }

  private inicializarFiltros(): void {
    // Filtro de estados
    this.estadosFiltrados$ = this.estadoAutocompleteControl.valueChanges.pipe(
      startWith(''),
      map(value => {
        const filterValue = typeof value === 'string' ? value : (value as Estado)?.label || '';
        return this._filtrarEstados(filterValue);
      })
    );

    // Filtro de cidades/municípios
    this.cidadesFiltradas$ = this.cidadeAutocompleteControl.valueChanges.pipe(
      startWith(''),
      map(value => {
        const filterValue = typeof value === 'string' ? value : (value as Cidade)?.label || '';
        return this._filtrarCidades(filterValue);
      })
    );

    // Filtro de escolas/instituições
    this.escolasFiltradas$ = this.escolaAutocompleteControl.valueChanges.pipe(
      startWith(''),
      map(value => {
        const filterValue = typeof value === 'string' ? value : (value as Escola)?.nomeInstituicao || '';
        return this._filtrarEscolas(filterValue);
      })
    );
  }

  //filtros para funcionalidade de busca por escrita
  private _filtrarEstados(value: string): Estado[] {
    if (!value) return this.estados;
    const filterValue = value.toLowerCase();
    return this.estados.filter(estado =>
      estado.label.toLowerCase().includes(filterValue)
    );
  }

  private _filtrarCidades(value: string): Cidade[] {
    if (!value) return this.cidades;
    const filterValue = value.toLowerCase();
    return this.cidades.filter(cidade =>
      cidade.label.toLowerCase().includes(filterValue)
    );
  }

  private _filtrarEscolas(value: string): Escola[] {
    if (!value) return this.escolas;
    const filterValue = value.toLowerCase();
    return this.escolas.filter(escola =>
      escola.nomeInstituicao.toLowerCase().includes(filterValue)
    );
  }

  // Funções de exibição para autocomplete
  displayEstado = (estado: Estado | null): string => {
    return estado?.label || '';
  }

  displayCidade = (cidade: Cidade | null): string => {
    return cidade?.label || '';
  }

  displayEscola = (escola: Escola | null): string => {
    return escola?.nomeInstituicao || '';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private carregarEstados(): void {
    this.carregandoEstados = true;
    this.formularioLocalizacao.get('estado')?.disable();

    this.localizacaoService.buscarEstados()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (estados) => {
          this.estados = estados;
          this.carregandoEstados = false;
          this.formularioLocalizacao.get('estado')?.enable();
        },
        error: (error) => {
          console.error('Erro ao carregar estados:', error);
          this.carregandoEstados = false;
          this.formularioLocalizacao.get('estado')?.enable();
        }
      });
  }

  private carregarDadosIniciais(): void {
    const localizacaoAtual = this.localizacaoService.obterLocalizacaoSelecionada();

    if (localizacaoAtual) {
      if (localizacaoAtual.estadoSelecionado) {
        this.formularioLocalizacao.patchValue({
          estado: localizacaoAtual.estadoSelecionado
        });

        // Carregar cidades do estado previamente selecionado
        this.carregarCidades(localizacaoAtual.estadoSelecionado);

        if (localizacaoAtual.cidadeSelecionada) {
          // Aguardar cidades carregarem antes de selecionar
          setTimeout(() => {
            this.formularioLocalizacao.patchValue({
              cidade: localizacaoAtual.cidadeSelecionada
            });
          }, 100);
        }
      }

      // Atualizar opções de plano educacional
      this.atualizarPlanosEducacionais();

      // Restaurar plano educacional selecionado se houver
      if (localizacaoAtual.planoEducacionalSelecionado) {
        setTimeout(() => {
          this.formularioLocalizacao.patchValue({
            planoEducacional: localizacaoAtual.planoEducacionalSelecionado
          });
        }, 150);
      } else if (localizacaoAtual.cidadeSelecionada && this.planosEducacionais.length > 0) {
        // Se não há plano selecionado mas há município, selecionar "Rede municipal" por padrão
        const redeMunicipal = this.planosEducacionais.find(plano =>
          plano.label.toLowerCase().includes('rede municipal')
        );
        if (redeMunicipal) {
          setTimeout(() => {
            this.formularioLocalizacao.patchValue({
              planoEducacional: redeMunicipal
            });
            this.onPlanoEducacionalMudou(redeMunicipal);
          }, 150);
        }
      }
    }
  }

  private onEstadoMudou(estadoSelecionado: Estado | null): void {
    // Limpar dados editados quando muda de estado
    this.limparDadosEditados();

    // Limpar cidade e plano educacional quando estado muda
    this.formularioLocalizacao.patchValue({
      cidade: null,
      planoEducacional: null
    });
    this.cidades = [];
    this.atualizarPlanosEducacionais(true); //forçar uso do formulário

    // Selecionar por padrão "Rede estadual de educação" quando um estado for selecionado
    if (estadoSelecionado && this.planosEducacionais.length > 0) {
      const redeEstadual = this.planosEducacionais.find(plano =>
        plano.label.toLowerCase().includes('rede estadual')
      );
      if (redeEstadual) {
        setTimeout(() => {
          this.formularioLocalizacao.patchValue({
            planoEducacional: redeEstadual
          });
          this.onPlanoEducacionalMudou(redeEstadual);
        }, 50);
      }
    }

    // Controle do estado disabled
    if (estadoSelecionado) {
      this.carregarCidades(estadoSelecionado);
    } else {
      this.formularioLocalizacao.get('cidade')?.disable();
    }

    this.salvarLocalizacao();
  }

  private onCidadeMudou(cidadeSelecionada: Cidade | null): void {
    // Limpar dados editados quando muda de cidade
    this.limparDadosEditados();

    // Limpar plano educacional quando cidade muda
    this.formularioLocalizacao.patchValue({ planoEducacional: null });
    this.atualizarPlanosEducacionais(true); // forçar uso do formulário

    // Selecionar por padrão "Rede municipal de educação" quando um município for selecionado
    if (cidadeSelecionada && this.planosEducacionais.length > 0) {
      const redeMunicipal = this.planosEducacionais.find(plano =>
        plano.label.toLowerCase().includes('rede municipal')
      );
      if (redeMunicipal) {
        setTimeout(() => {
          this.formularioLocalizacao.patchValue({
            planoEducacional: redeMunicipal
          });
          this.onPlanoEducacionalMudou(redeMunicipal);
        }, 50);
      }
    }

    // Carregar escolas se estiver em modo filtrar-escolas e tiver município selecionado
    if (this.mostrarSeletorEscolas && cidadeSelecionada) {
      this.carregarEscolas();
    } else {
      // Limpar escolas se município foi desmarcado
      this.escolas = [];
      this.formularioLocalizacao.patchValue({ escola: null });
      this.formularioLocalizacao.get('escola')?.disable();
    }

    this.salvarLocalizacao();
  }

  private onPlanoEducacionalMudou(planoSelecionado: PlanoEducacional | null): void {
    this.salvarLocalizacao();
  }

  //Controla os comportamentos do formulário
  private atualizarPlanosEducacionais(usarFormulario: boolean = false): void {
    let localizacaoAtual: LocalizacaoSelecionada;

    if (usarFormulario) {
      localizacaoAtual = this.obterLocalizacaoAtual();
    } else {
      // Ao abrir dropdown sem mudanças, tenta sessionStorage primeiro
      const localizacaoSalva = this.localizacaoService.obterLocalizacaoSelecionada();
      localizacaoAtual = localizacaoSalva || this.obterLocalizacaoAtual();
    }

    // No R3 e R4, nível municipal tem apenas "Plano da rede municipal"
    this.planosEducacionais =
      this.localizacaoService.obterOpcoesPlanoEducacional(
        localizacaoAtual,
        this.mostrarSeletorEscolas,
        this.ehR3,
        this.ehR5,
      );

    // Controle do estado disabled
    if (this.planosEducacionais.length > 0) {
      this.formularioLocalizacao.get('planoEducacional')?.enable();

      // Se houver apenas uma opção, seleciona automaticamente
      if (this.planosEducacionais.length === 1) {
        this.formularioLocalizacao.patchValue({
          planoEducacional: this.planosEducacionais[0]
        });
        this.onPlanoEducacionalMudou(this.planosEducacionais[0]);
      }
    } else {
      this.formularioLocalizacao.get('planoEducacional')?.disable();
    }

    // Forçar detecção de mudanças
    this.cdr.detectChanges();
  }

  private obterLocalizacaoAtual(): LocalizacaoSelecionada {
    const valores = this.formularioLocalizacao.value;
    return {
      estadoSelecionado: valores.estado,
      cidadeSelecionada: valores.cidade,
      planoEducacionalSelecionado: valores.planoEducacional
    };
  }

  private carregarCidades(estado: Estado): void {
    this.carregandoCidades = true;
    this.formularioLocalizacao.get('cidade')?.disable();

    this.localizacaoService.buscarCidades(estado)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (cidades) => {
          this.cidades = cidades;
          this.carregandoCidades = false;
          if (this.podeSelecionarCidade) {
            this.formularioLocalizacao.get('cidade')?.enable();
          }
        },
        error: (error) => {
          console.error('Erro ao carregar cidades:', error);
          this.carregandoCidades = false;
          if (this.podeSelecionarCidade) {
            this.formularioLocalizacao.get('cidade')?.enable();
          }
        }
      });
  }

  private carregarEscolas(): void {
    const valores = this.formularioLocalizacao.value;
    const cidadeSelecionada = valores.cidade;
    const planoSelecionado = valores.planoEducacional;

    if (!cidadeSelecionada) {
      return;
    }

    this.carregandoEscolas = true;
    this.formularioLocalizacao.get('escola')?.disable();

    // Passar tipo de simulação para buscar escolas corretas (plano vs rede)
    const tipoSimulacao = planoSelecionado?.value;

    this.escolasService.buscarEscolasPorMunicipio(cidadeSelecionada.value, tipoSimulacao)
      .then((escolas) => {
        this.escolas = escolas;
        this.carregandoEscolas = false;
        this.formularioLocalizacao.get('escola')?.enable();
      })
      .catch((error) => {
        console.error('Erro ao carregar escolas:', error);
        this.carregandoEscolas = false;
        this.escolas = [];
      });
  }

  private onEscolaMudou(escolaSelecionada: Escola | null): void {
    if (
      this.featureFlagService.funcionalidadeHabilitada('1436-inserir-edicao-nivel-escola') &&
      escolaSelecionada?.codMec !== this.localizacaoService.obterEscolaSelecionada()?.codMec
    ) {
      this.limparDadosEditados();
    }

    // Salvar escola selecionada no LocalizacaoService
    this.localizacaoService.salvarEscolaSelecionada(escolaSelecionada);
    this.salvarLocalizacao();
  }

  private salvarLocalizacao(): void {
    const valores = this.formularioLocalizacao.value;

    // Usa o plano selecionado pelo usuário (tanto R2/R3 quanto R4)
    const tipoSimulacao: number = valores.planoEducacional?.value || TipoSimulacao.planejamento;

    // Preservar escola selecionada (se houver) ao salvar localização
    const localizacaoAtual = this.localizacaoService.obterLocalizacaoSelecionada();

    const localizacao: LocalizacaoSelecionada = {
      estadoSelecionado: valores.estado,
      cidadeSelecionada: valores.cidade,
      cidadeSelecionadaId: valores.cidade?.value,
      planoEducacionalSelecionado: valores.planoEducacional,
      tipoSimulacaoSelecionado: tipoSimulacao,
      escolaSelecionada: valores.escola || localizacaoAtual?.escolaSelecionada // Preserva escola
    };

    this.localizacaoService.salvarLocalizacaoSelecionada(localizacao);
    this.localizacaoService.salvarTipoSimulacao(localizacao.tipoSimulacaoSelecionado!);
  }

  onSimular(): void {
    const localizacaoAtual = this.localizacaoService.obterLocalizacaoSelecionada();

    if (!localizacaoAtual || !this.localizacaoService.validarLocalizacaoCompleta(localizacaoAtual)) {
      console.warn('Localização incompleta para simulação');
      return;
    }

    // Navegar para rota diferente baseada na URL atual
    setTimeout(() => {
      switch (this.router.url) {
        case '/filtrar-instituicoes':
          this.router.navigate([
            '/resultado/relatorio-financiamento-das-escolas',
          ]);
          break;
        case '/selecionar-local':
          this.router.navigate(['/resultado/projecao-orcamentaria']);
          break;
        case '/filtrar-escolas':
          this.router.navigate(['/resultado/relatorio-custo-por-escola']);
          break;
        case '/selecionar-local-R5':
          if (
            this.featureFlagService.funcionalidadeHabilitada(
              '884-tela-relatorio-receitas-despesas',
            )
          ) {
            this.router.navigate(['/resultado/relatorio-receitas-despesas']);
            return;
          } else {
            this.router.navigate(['/']);
            return;
          }

        default:
          this.router.navigate(['/']);
      }
    }, 0);
  }

  get podeSelecionarCidade(): boolean {
    return !!this.formularioLocalizacao?.get('estado')?.value && !this.carregandoCidades;
  }

  get podeSelecionarPlanoEducacional(): boolean {
    return this.planosEducacionais.length > 0;
  }

  compareEstados(e1: Estado, e2: Estado): boolean {
    return e1 && e2 ? e1.value === e2.value : e1 === e2;
  }

  compareCidades(c1: Cidade, c2: Cidade): boolean {
    return c1 && c2 ? c1.value === c2.value : c1 === c2;
  }

  comparePlanosEducacionais(p1: PlanoEducacional, p2: PlanoEducacional): boolean {
    return p1 && p2 ? p1.value === p2.value : p1 === p2;
  }

  compareEscolas(e1: Escola, e2: Escola): boolean {
    return e1 && e2 ? e1.codMec === e2.codMec : e1 === e2;
  }

  // Novos métodos para interação com os inputs

  abrirSelectEstado(): void {
    if (!this.carregandoEstados && this.selectEstado && !this.selectEstado.disabled) {
      setTimeout(() => {
        this.selectEstado.open();
      }, 10);
    }
  }

  abrirSelectCidade(): void {
    if (this.podeSelecionarCidade && !this.carregandoCidades && this.selectCidade && !this.selectCidade.disabled) {
      setTimeout(() => {
        this.selectCidade.open();
      }, 10);
    }
  }

  abrirSelectPlanoEducacional(): void {
    if (this.podeSelecionarPlanoEducacional && this.selectPlanoEducacional && !this.selectPlanoEducacional.disabled) {
      setTimeout(() => {
        this.selectPlanoEducacional.open();
      }, 10);
    }
  }

  abrirSelectEscola(): void {
    if (this.podeSelecionarEscola && !this.carregandoEscolas && this.selectEscola && !this.selectEscola.disabled) {
      setTimeout(() => {
        this.selectEscola.open();
      }, 10);
    }
  }

  onEstadoSelecionado(estado: Estado | null): void {
    // Atualizar o form control diretamente
    this.formularioLocalizacao.patchValue({ estado: estado }, { emitEvent: false });
    // Limpar o input de autocomplete para voltar ao placeholder
    this.estadoAutocompleteControl.setValue('', { emitEvent: false });
    this.onEstadoMudou(estado);
    this.cdr.detectChanges();

    // Remover foco do input
    setTimeout(() => {
      if (this.inputEstadoAutocomplete) {
        this.inputEstadoAutocomplete.nativeElement.blur();
      }
    }, 0);
  }

  onCidadeSelecionada(cidade: Cidade | null): void {
    // Atualizar o form control diretamente
    this.formularioLocalizacao.patchValue({ cidade: cidade }, { emitEvent: false });
    // Limpar o input de autocomplete para voltar ao placeholder
    this.cidadeAutocompleteControl.setValue('', { emitEvent: false });
    this.onCidadeMudou(cidade);
    this.cdr.detectChanges();

    // Remover foco do input
    setTimeout(() => {
      if (this.inputCidadeAutocomplete) {
        this.inputCidadeAutocomplete.nativeElement.blur();
      }
    }, 0);
  }

  onPlanoEducacionalSelecionado(plano: PlanoEducacional | null): void {
    // Atualizar o form control diretamente
    this.formularioLocalizacao.patchValue({ planoEducacional: plano }, { emitEvent: false });
    this.onPlanoEducacionalMudou(plano);
    this.cdr.detectChanges();
  }

  onEscolaSelecionada(escola: Escola | null): void {
    // Atualizar o form control diretamente
    this.formularioLocalizacao.patchValue({ escola: escola }, { emitEvent: false });
    // Limpar o input de autocomplete
    this.escolaAutocompleteControl.setValue('', { emitEvent: false });
    this.onEscolaMudou(escola);
    this.cdr.detectChanges();

    // Remover foco do input
    setTimeout(() => {
      if (this.inputEscolaAutocomplete) {
        this.inputEscolaAutocomplete.nativeElement.blur();
      }
    }, 0);
  }

  get estadoSelecionadoTexto(): string {
    const estadoValue = this.formularioLocalizacao?.get('estado')?.value;

    if (this.carregandoEstados) {
      return 'Carregando...';
    }

    if (estadoValue && estadoValue.label) {
      return estadoValue.label;
    }

    if (estadoValue === null) {
      return 'Nível Nacional';
    }

    return 'Selecione um estado';
  }

  get cidadeSelecionadaTexto(): string {
    const cidadeValue = this.formularioLocalizacao?.get('cidade')?.value;
    const estadoValue = this.formularioLocalizacao?.get('estado')?.value;

    if (this.carregandoCidades) {
      return 'Carregando...';
    }

    if (cidadeValue && cidadeValue.label) {
      return cidadeValue.label;
    }

    if (cidadeValue === null && estadoValue) {
      return 'Todo o estado';
    }

    if (!estadoValue) {
      return 'Selecione um estado primeiro';
    }

    return 'Selecione um município';
  }

  get planoEducacionalSelecionadoTexto(): string {
    const planoValue = this.formularioLocalizacao?.get('planoEducacional')?.value;

    if (planoValue && planoValue.label) {
      return planoValue.label;
    }

    if (this.planosEducacionais.length === 0) {
      return 'Selecione uma localização primeiro';
    }

    return 'Selecione um plano educacional';
  }

  get escolaSelecionadaTexto(): string {
    const escolaValue = this.formularioLocalizacao?.get('escola')?.value;
    const cidadeValue = this.formularioLocalizacao?.get('cidade')?.value;

    if (this.carregandoEscolas) {
      return 'Carregando...';
    }

    if (escolaValue && escolaValue.nomeInstituicao) {
      return escolaValue.nomeInstituicao;
    }

    if (escolaValue === null && cidadeValue) {
      return 'Todas as escolas do município';
    }

    if (!cidadeValue) {
      return 'Selecione um município primeiro';
    }

    return 'Selecione uma escola (opcional)';
  }

  // Verifica se há texto digitado nos autocompletes (para esconder placeholder)
  get estadoTemTexto(): boolean {
    const valor = this.estadoAutocompleteControl.value;
    return typeof valor === 'string' && valor.length > 0;
  }

  get cidadeTemTexto(): boolean {
    const valor = this.cidadeAutocompleteControl.value;
    return typeof valor === 'string' && valor.length > 0;
  }

  get escolaTemTexto(): boolean {
    const valor = this.escolaAutocompleteControl.value;
    return typeof valor === 'string' && valor.length > 0;
  }

  get podeSelecionarEscola(): boolean {
    return this.mostrarSeletorEscolas &&
           !!this.formularioLocalizacao?.get('cidade')?.value &&
           !this.carregandoEscolas &&
           this.escolas.length > 0;
  }

  //Limpa todos os dados editados nos StateServices quando o usuário muda de localização.
  private limparDadosEditados(): void {
    this.condicaoQualidadeState.limparValoresEditados();
    this.acessoOfertaEducacionalState.limparValoresEditados();
    this.precoEncargosAdicionaisState.limparValoresEditados();
  }
}
